import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';

const PricingSection = () => {
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

    const plans = [
        {
            name: "Core",
            price: "Free",
            period: "",
            features: ["5 Pipelines", "Basic Nodes", "Community Support", "1GB Storage"],
            color: "border-gray-700",
            hoverColor: "hover:border-gray-500",
            buttonStyle: "bg-gray-800 hover:bg-gray-700 border border-gray-600"
        },
        {
            name: "Pro",
            price: "$49",
            period: "/mo",
            features: [
                "Unlimited Pipelines",
                "Advanced AI Nodes",
                "Priority Support",
                "100GB Storage",
                "Team Collaboration"
            ],
            color: "border-blue-500",
            hoverColor: "hover:border-blue-400",
            buttonStyle: "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500",
            bestValue: true
        },
        {
            name: "Enterprise",
            price: "Custom",
            period: "",
            features: [
                "Dedicated Infrastructure",
                "Custom Node Development",
                "24/7 SLA Support",
                "Unlimited Storage",
                "On-Premise Deployment"
            ],
            color: "border-purple-500",
            hoverColor: "hover:border-purple-400",
            buttonStyle: "bg-gray-800 hover:bg-gray-700 border border-purple-500/50"
        }
    ];

    return (
        <section
            ref={sectionRef}
            className="py-32 bg-cyber-black text-center relative z-10 border-t border-gray-800 overflow-hidden"
        >
            {/* Background Gradient Orbs */}
            <div className="absolute inset-0 pointer-events-none">
                <motion.div
                    className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10"
                    style={{
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)',
                        filter: 'blur(60px)'
                    }}
                    animate={{
                        y: [0, -40, 0],
                        x: [0, 30, 0],
                    }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <motion.div
                    className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-10"
                    style={{
                        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%)',
                        filter: 'blur(60px)'
                    }}
                    animate={{
                        y: [0, 50, 0],
                        x: [0, -40, 0],
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 2
                    }}
                />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="mb-6"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-6">
                        <span className="text-sm font-medium text-blue-300 tracking-wide uppercase">
                            Flexible Pricing
                        </span>
                    </div>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
                >
                    <span className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                        Choose Your Plan
                    </span>
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-16"
                >
                    Scale seamlessly from prototype to production.
                    <br />
                    <span className="text-blue-300">Start free, upgrade when you're ready.</span>
                </motion.p>

                {/* Pricing Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{
                                duration: 0.6,
                                delay: 0.6 + (index * 0.15),
                                ease: "easeOut"
                            }}
                            whileHover={{
                                y: -10,
                                transition: { duration: 0.3 }
                            }}
                            className={`relative bg-gray-900/80 backdrop-blur-md border-2 ${plan.color} ${plan.hoverColor} rounded-lg p-8 flex flex-col shadow-lg transition-all duration-300 ${plan.bestValue ? 'md:scale-105' : ''
                                }`}
                        >
                            {/* Best Value Badge */}
                            {plan.bestValue && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                                    transition={{ delay: 1, duration: 0.5 }}
                                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-lg font-bold text-sm shadow-lg animate-pulse"
                                >
                                    BEST VALUE
                                </motion.div>
                            )}

                            {/* Plan Name */}
                            <h3 className="text-2xl font-bold mb-2 text-white">{plan.name}</h3>

                            {/* Price */}
                            <div className="mb-8">
                                <span className="text-5xl font-bold text-white">{plan.price}</span>
                                {plan.period && (
                                    <span className="text-gray-400 text-lg">{plan.period}</span>
                                )}
                            </div>

                            {/* Features List */}
                            <ul className="flex-1 space-y-4 mb-8 text-left">
                                {plan.features.map((feature, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.8 + (index * 0.15) + (i * 0.05)
                                        }}
                                        className="flex items-start text-gray-300"
                                    >
                                        <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                    </motion.li>
                                ))}
                            </ul>

                            {/* CTA Button */}
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 ${plan.buttonStyle} shadow-lg`}
                            >
                                {plan.name === 'Enterprise' ? 'Contact Sales' : `Get Started`}
                            </motion.button>
                        </motion.div>
                    ))}
                </div>

                {/* Trust Badge */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 1, delay: 1.2 }}
                    className="mt-16 text-center"
                >
                    <p className="text-gray-500 text-sm">
                        💳 All plans include a <span className="text-blue-400 font-semibold">14-day money-back guarantee</span>
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default PricingSection;
